import type { InferInsertModel, InferSelectModel } from "drizzle-orm";
import { sql } from "drizzle-orm";
import {
	foreignKey,
	index,
	jsonb,
	numeric,
	pgTable,
	text,
	unique,
} from "drizzle-orm/pg-core";
import { sqlNow } from "../../db/utils.js";
import { checkouts } from "../checkouts/checkoutTable.js";
import { customers } from "../cusModels/cusTable.js";
import { organizations } from "../orgModels/orgTable.js";
import type {
	DomesticPaymentEventType,
	DomesticPaymentFulfillmentStatus,
	DomesticPaymentIntentStatus,
	DomesticPaymentProvider,
	DomesticPaymentProviderAction,
} from "./domesticPaymentModels.js";

export type DomesticPaymentProcessorConfigData = {
	app_id?: string;
	mch_id?: string;
	merchant_private_key_ref?: string;
	platform_public_key_ref?: string;
	api_v3_key_ref?: string;
	notify_url?: string;
	return_url?: string;
	enabled_products?: string[];
};

export type DomesticPaymentIntentSnapshot = {
	action: string;
	params_version?: number;
	params: unknown;
	billing_plan?: unknown;
	invoice?: {
		stripe_invoice_id?: string;
		paid_out_of_band?: boolean;
	};
};

export const domesticPaymentProcessorConfigs = pgTable(
	"domestic_payment_processor_configs",
	{
		id: text().primaryKey().notNull(),
		org_id: text().notNull(),
		env: text().notNull(),
		provider: text().$type<DomesticPaymentProvider>().notNull(),
		status: text().notNull().default("disabled"),
		config: jsonb().$type<DomesticPaymentProcessorConfigData>().notNull(),
		created_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		updated_at: numeric({ mode: "number" }).notNull().default(sqlNow),
	},
	(table) => [
		foreignKey({
			columns: [table.org_id],
			foreignColumns: [organizations.id],
			name: "domestic_payment_processor_configs_org_id_fkey",
		}).onDelete("cascade"),
		unique("domestic_payment_processor_configs_org_env_provider_unique").on(
			table.org_id,
			table.env,
			table.provider,
		),
		index("idx_domestic_payment_processor_configs_org_env").on(
			table.org_id,
			table.env,
		),
	],
);

export const domesticPaymentIntents = pgTable(
	"domestic_payment_intents",
	{
		id: text().primaryKey().notNull(),
		org_id: text().notNull(),
		env: text().notNull(),
		internal_customer_id: text().notNull(),
		customer_id: text().notNull(),
		checkout_id: text(),
		provider: text().$type<DomesticPaymentProvider>().notNull(),
		status: text().$type<DomesticPaymentIntentStatus>().notNull(),
		amount: numeric({ mode: "number" }).notNull(),
		currency: text().notNull(),
		provider_order_id: text().notNull(),
		provider_transaction_id: text(),
		stripe_invoice_id: text(),
		provider_action: jsonb().$type<DomesticPaymentProviderAction>(),
		snapshot: jsonb().$type<DomesticPaymentIntentSnapshot>().notNull(),
		metadata: jsonb().$type<Record<string, unknown>>().default({}),
		created_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		updated_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		expires_at: numeric({ mode: "number" }),
		paid_at: numeric({ mode: "number" }),
		fulfilled_at: numeric({ mode: "number" }),
	},
	(table) => [
		foreignKey({
			columns: [table.org_id],
			foreignColumns: [organizations.id],
			name: "domestic_payment_intents_org_id_fkey",
		}).onDelete("cascade"),
		foreignKey({
			columns: [table.internal_customer_id],
			foreignColumns: [customers.internal_id],
			name: "domestic_payment_intents_internal_customer_id_fkey",
		}).onDelete("cascade"),
		foreignKey({
			columns: [table.checkout_id],
			foreignColumns: [checkouts.id],
			name: "domestic_payment_intents_checkout_id_fkey",
		}).onDelete("set null"),
		unique("domestic_payment_intents_provider_order_unique").on(
			table.org_id,
			table.env,
			table.provider,
			table.provider_order_id,
		),
		index("idx_domestic_payment_intents_customer").on(
			table.org_id,
			table.env,
			table.internal_customer_id,
		),
		index("idx_domestic_payment_intents_status").on(
			table.org_id,
			table.env,
			table.status,
		),
		index("idx_domestic_payment_intents_checkout").on(table.checkout_id),
		index("idx_domestic_payment_intents_transaction").on(
			table.provider_transaction_id,
		),
	],
);

export const domesticPaymentEvents = pgTable(
	"domestic_payment_events",
	{
		id: text().primaryKey().notNull(),
		org_id: text().notNull(),
		env: text().notNull(),
		intent_id: text().notNull(),
		provider: text().$type<DomesticPaymentProvider>().notNull(),
		event_type: text().$type<DomesticPaymentEventType>().notNull(),
		provider_event_id: text(),
		idempotency_key: text().notNull(),
		raw_payload: jsonb().$type<Record<string, unknown>>(),
		parsed_payload: jsonb().$type<Record<string, unknown>>(),
		created_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		processed_at: numeric({ mode: "number" }),
	},
	(table) => [
		foreignKey({
			columns: [table.intent_id],
			foreignColumns: [domesticPaymentIntents.id],
			name: "domestic_payment_events_intent_id_fkey",
		}).onDelete("cascade"),
		unique("domestic_payment_events_idempotency_unique").on(
			table.org_id,
			table.env,
			table.provider,
			table.idempotency_key,
		),
		index("idx_domestic_payment_events_intent").on(table.intent_id),
		index("idx_domestic_payment_events_provider_event").on(
			table.provider_event_id,
		),
	],
);

export const domesticPaymentRefunds = pgTable(
	"domestic_payment_refunds",
	{
		id: text().primaryKey().notNull(),
		org_id: text().notNull(),
		env: text().notNull(),
		intent_id: text().notNull(),
		provider: text().$type<DomesticPaymentProvider>().notNull(),
		status: text().notNull(),
		amount: numeric({ mode: "number" }).notNull(),
		currency: text().notNull(),
		reason: text(),
		provider_refund_id: text().notNull(),
		provider_payload: jsonb().$type<Record<string, unknown>>(),
		created_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		updated_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		succeeded_at: numeric({ mode: "number" }),
	},
	(table) => [
		foreignKey({
			columns: [table.intent_id],
			foreignColumns: [domesticPaymentIntents.id],
			name: "domestic_payment_refunds_intent_id_fkey",
		}).onDelete("cascade"),
		unique("domestic_payment_refunds_provider_refund_unique").on(
			table.org_id,
			table.env,
			table.provider,
			table.provider_refund_id,
		),
		index("idx_domestic_payment_refunds_intent").on(table.intent_id),
	],
);

export const domesticPaymentFulfillments = pgTable(
	"domestic_payment_fulfillments",
	{
		id: text().primaryKey().notNull(),
		org_id: text().notNull(),
		env: text().notNull(),
		intent_id: text().notNull(),
		status: text().$type<DomesticPaymentFulfillmentStatus>().notNull(),
		idempotency_key: text().notNull(),
		started_at: numeric({ mode: "number" }),
		completed_at: numeric({ mode: "number" }),
		error: jsonb().$type<Record<string, unknown>>(),
		result: jsonb().$type<Record<string, unknown>>(),
		created_at: numeric({ mode: "number" }).notNull().default(sqlNow),
		updated_at: numeric({ mode: "number" }).notNull().default(sqlNow),
	},
	(table) => [
		foreignKey({
			columns: [table.intent_id],
			foreignColumns: [domesticPaymentIntents.id],
			name: "domestic_payment_fulfillments_intent_id_fkey",
		}).onDelete("cascade"),
		unique("domestic_payment_fulfillments_intent_unique").on(table.intent_id),
		unique("domestic_payment_fulfillments_idempotency_unique").on(
			table.org_id,
			table.env,
			table.idempotency_key,
		),
		index("idx_domestic_payment_fulfillments_pending")
			.on(table.org_id, table.env, table.created_at)
			.where(sql`${table.status} IN ('pending', 'running')`),
	],
);

export type DomesticPaymentProcessorConfig = InferSelectModel<
	typeof domesticPaymentProcessorConfigs
>;
export type InsertDomesticPaymentProcessorConfig = InferInsertModel<
	typeof domesticPaymentProcessorConfigs
>;
export type DomesticPaymentIntent = InferSelectModel<
	typeof domesticPaymentIntents
>;
export type InsertDomesticPaymentIntent = InferInsertModel<
	typeof domesticPaymentIntents
>;
export type DomesticPaymentEvent = InferSelectModel<typeof domesticPaymentEvents>;
export type InsertDomesticPaymentEvent = InferInsertModel<
	typeof domesticPaymentEvents
>;
export type DomesticPaymentRefund = InferSelectModel<
	typeof domesticPaymentRefunds
>;
export type InsertDomesticPaymentRefund = InferInsertModel<
	typeof domesticPaymentRefunds
>;
export type DomesticPaymentFulfillment = InferSelectModel<
	typeof domesticPaymentFulfillments
>;
export type InsertDomesticPaymentFulfillment = InferInsertModel<
	typeof domesticPaymentFulfillments
>;
